import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  Check,
  Download,
  Eye,
  FileArchive,
  FileSpreadsheet,
  FileText,
  Files,
  Loader2,
  Music,
  Video,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import VisualizadorDeArquivo from "../VisualizadorDeArquivo";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Galeria de arquivos do atendimento.
//
// Reencontrar o comprovante, o PDF do contrato ou o áudio que o cliente mandou
// "semana passada" rolando a conversa é o que esta tela evita: mostra só os
// anexos, do mais recente para o mais antigo, e deixa levar vários — ou a
// conversa inteira — num ZIP.
const FILTROS = [
  { chave: "todos", rotulo: "Todos" },
  { chave: "imagem", rotulo: "Imagens" },
  { chave: "documento", rotulo: "Documentos" },
  { chave: "audio", rotulo: "Áudios" },
  { chave: "video", rotulo: "Vídeos" },
  // Link não é anexo: vem de outra lista, e por isso a grade dá lugar a uma
  // lista de endereços quando este filtro está escolhido.
  { chave: "link", rotulo: "Links" },
];

const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

const IconeDoArquivo = ({ arquivo, className }) => {
  if (arquivo.categoria === "audio") return <Music className={className} />;
  if (arquivo.categoria === "video") return <Video className={className} />;
  if (PLANILHAS.includes(arquivo.extensao))
    return <FileSpreadsheet className={className} />;
  if (COMPACTADOS.includes(arquivo.extensao))
    return <FileArchive className={className} />;
  return <FileText className={className} />;
};

const rotuloDoArquivo = (arquivo) => {
  if (arquivo.legenda) return arquivo.legenda;
  const ext = (arquivo.extensao || "").replace(".", "").toUpperCase();
  return ext ? `Arquivo ${ext}` : "Arquivo";
};

const TicketMediaGallery = ({ open, onClose, ticketId }) => {
  const [arquivos, setArquivos] = useState([]);
  const [links, setLinks] = useState([]);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [filtro, setFiltro] = useState("todos");
  const [carregando, setCarregando] = useState(false);
  const [baixando, setBaixando] = useState(null);
  const [visualizando, setVisualizando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get(`/messages/${ticketId}/gallery`);
      setArquivos(data.arquivos || []);
      setLinks(data.links || []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (!open) return;

    // Zera a seleção a cada abertura: manter o que foi marcado antes faria a
    // pessoa baixar sem querer arquivos que ela já nem lembra ter marcado.
    setSelecionados(new Set());
    setFiltro("todos");
    carregar();
  }, [open, carregar]);

  const contagem = useMemo(() => {
    const total = { todos: arquivos.length, link: links.length };
    arquivos.forEach((a) => {
      total[a.categoria] = (total[a.categoria] || 0) + 1;
    });
    return total;
  }, [arquivos, links]);

  const visiveis = useMemo(
    () =>
      filtro === "todos"
        ? arquivos
        : arquivos.filter((a) => a.categoria === filtro),
    [arquivos, filtro],
  );

  const alternar = (id) => {
    setSelecionados((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  // "Selecionar todas" vale para o que está à vista: com o filtro em
  // Documentos, marcar tudo e baixar não pode arrastar junto as 200 imagens.
  const todosMarcados =
    visiveis.length > 0 && visiveis.every((a) => selecionados.has(a.id));

  const alternarTodos = () => {
    setSelecionados((anterior) => {
      const novo = new Set(anterior);
      visiveis.forEach((a) => {
        if (todosMarcados) novo.delete(a.id);
        else novo.add(a.id);
      });
      return novo;
    });
  };

  const baixar = async (ids, rotulo) => {
    if (!ids.length) return;

    setBaixando(rotulo);
    try {
      // `blob`: a resposta é um ZIP. Sem isto o axios tenta ler como texto e o
      // arquivo chega corrompido.
      const { data, headers } = await api.post(
        `/messages/${ticketId}/gallery/download`,
        { messageIds: ids },
        { responseType: "blob" },
      );

      const nome =
        (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] ||
        `arquivos-atendimento-${ticketId}.zip`;

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(
        `${ids.length} ${
          ids.length === 1 ? "arquivo" : "arquivos"
        } no ZIP. O download começou.`,
      );
    } catch (err) {
      // Com responseType blob a mensagem de erro também chega como blob.
      if (err?.response?.data instanceof Blob) {
        try {
          const texto = await err.response.data.text();
          err.response.data = JSON.parse(texto);
        } catch {
          // Fica o erro genérico mesmo.
        }
      }
      toastError(err);
    } finally {
      setBaixando(null);
    }
  };

  const rotuloBaixarTudo =
    filtro === "todos"
      ? `Baixar tudo (${arquivos.length})`
      : `Baixar ${FILTROS.find((f) => f.chave === filtro).rotulo.toLowerCase()} (${
          visiveis.length
        })`;

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Files className="h-5 w-5" />
            Arquivos do atendimento
          </DialogTitle>
          <DialogDescription>
            Imagens, PDFs, planilhas, áudios e vídeos da conversa. Clique para
            marcar e baixe em um arquivo ZIP.
          </DialogDescription>
        </DialogHeader>

        {carregando ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : arquivos.length === 0 && links.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Esta conversa ainda não tem arquivos nem links.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1">
              {FILTROS.map((f) => {
                const quantos = contagem[f.chave] || 0;
                if (f.chave !== "todos" && !quantos) return null;

                return (
                  <Button
                    key={f.chave}
                    variant={filtro === f.chave ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFiltro(f.chave)}
                  >
                    {f.rotulo} ({quantos})
                  </Button>
                );
              })}
            </div>

            {filtro === "link" ? (
              <ul className="max-h-[55vh] divide-y overflow-y-auto rounded-lg border">
                {links.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Nenhum link nesta conversa.
                  </li>
                )}
                {links.map((link) => (
                  <li key={`${link.messageId}-${link.url}`}>
                    <a
                      href={
                        link.url.startsWith("http")
                          ? link.url
                          : `https://${link.url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 py-2 hover:bg-muted/60"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <LinkIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
                        <span className="truncate">{link.dominio}</span>
                        <span className="ml-auto shrink-0 text-[11px] font-normal text-muted-foreground">
                          {format(parseISO(link.createdAt), "dd/MM/yyyy")}
                        </span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {link.url}
                      </span>
                      {link.trecho && (
                        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground/80">
                          {link.fromMe ? "Equipe: " : ""}
                          {link.trecho}
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={alternarTodos}
                disabled={!visiveis.length}
              >
                {todosMarcados ? "Desmarcar todos" : "Selecionar todos"}
              </Button>
              <span className="text-xs text-muted-foreground">
                {selecionados.size} marcado
                {selecionados.size === 1 ? "" : "s"} · {visiveis.length} à
                vista
              </span>
            </div>

            <div className="grid max-h-[55vh] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 md:grid-cols-4">
              {visiveis.map((arquivo) => {
                const marcado = selecionados.has(arquivo.id);
                const miniatura = arquivo.categoria === "imagem";

                return (
                  <div key={arquivo.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => alternar(arquivo.id)}
                      aria-pressed={marcado}
                      title={arquivo.legenda || rotuloDoArquivo(arquivo)}
                      className={cn(
                        "relative aspect-square w-full overflow-hidden rounded-md border bg-muted",
                        marcado && "ring-2 ring-primary ring-offset-1",
                      )}
                    >
                      {miniatura ? (
                        <img
                          src={arquivo.url}
                          alt={arquivo.legenda || "Imagem do atendimento"}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
                          <IconeDoArquivo
                            arquivo={arquivo}
                            className="h-8 w-8 text-muted-foreground"
                          />
                          <span className="line-clamp-2 text-[11px] text-foreground">
                            {rotuloDoArquivo(arquivo)}
                          </span>
                          {arquivo.extensao && (
                            <span className="text-[10px] uppercase text-muted-foreground">
                              {arquivo.extensao.replace(".", "")}
                            </span>
                          )}
                        </span>
                      )}

                      {marcado && (
                        <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                      <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 py-0.5 text-[10px] text-white">
                        {format(parseISO(arquivo.createdAt), "dd/MM HH:mm")} ·{" "}
                        {arquivo.fromMe ? "enviada" : "recebida"}
                      </span>
                    </button>

                    {/* Ver um arquivo só, sem passar pelo ZIP — é o que a
                        pessoa quer quando achou o PDF que procurava. Abre no
                        visualizador, que sabe mostrar PDF, vídeo e áudio, e
                        não só imagem. */}
                    <button
                      type="button"
                      onClick={() =>
                        setVisualizando(
                          visiveis.findIndex((v) => v.id === arquivo.id),
                        )
                      }
                      title="Visualizar"
                      className="absolute left-1 top-1 rounded bg-black/55 p-1 text-white opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 hover:opacity-100"
                    >
                      <Eye className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>

              </>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
              <Button
                variant="outline"
                className={cn(filtro === "link" && "hidden")}
                onClick={() =>
                  baixar(
                    visiveis.map((a) => a.id),
                    "tudo",
                  )
                }
                disabled={!visiveis.length || !!baixando}
              >
                {baixando === "tudo" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {rotuloBaixarTudo}
              </Button>
              <Button
                className={cn(filtro === "link" && "hidden")}
                onClick={() =>
                  baixar(Array.from(selecionados), "selecionados")
                }
                disabled={!selecionados.size || !!baixando}
              >
                {baixando === "selecionados" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Baixar selecionados ({selecionados.size})
              </Button>
            </div>
          </>
        )}

        <VisualizadorDeArquivo
          open={visualizando !== null && visualizando >= 0}
          onClose={() => setVisualizando(null)}
          arquivos={visiveis}
          indice={visualizando ?? 0}
          onIndice={setVisualizando}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TicketMediaGallery;
